import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { SamenMeditation } from '@/types/custom';

export function useSamenMeditations(samenId: string | undefined) {
  const [meditations, setMeditations] = useState<SamenMeditation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeMeditations, setActiveMeditations] =
    useState<SamenMeditation[]>([]);

  const fetchMeditations = useCallback(async () => {
    if (!samenId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('samen_meditations')
      .select('*')
      .eq('samen_id', samenId)
      .order('scheduled_at', { ascending: true });

    if (!error && data) {
      const now = new Date();
      const relevant = data.filter((m) => {
        const end = new Date(m.scheduled_at);
        end.setMinutes(end.getMinutes() + (m.duration_minutes ?? 30));
        return end > now;
      });
      setMeditations(relevant);

      // Find all currently active meditations (open player 30s early)
      const active = relevant.filter((m) => {
        const start = new Date(m.scheduled_at);
        start.setSeconds(start.getSeconds() - 30);
        const end = new Date(m.scheduled_at);
        end.setMinutes(end.getMinutes() + (m.duration_minutes ?? 30));
        return now >= start && now < end;
      });
      setActiveMeditations(active);
    }
    setLoading(false);
  }, [samenId]);

  useEffect(() => {
    fetchMeditations();
    const interval = setInterval(fetchMeditations, 5_000);
    return () => clearInterval(interval);
  }, [fetchMeditations]);

  const createMeditation = async (meditation: Omit<SamenMeditation, 'id' | 'created_at'>) => {
    const { data, error } = await supabase
      .from('samen_meditations')
      .insert(meditation)
      .select()
      .single();

    if (error) throw error;
    await fetchMeditations();
    return data;
  };

  const deleteMeditation = async (id: string) => {
    const { error } = await supabase
      .from('samen_meditations')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await fetchMeditations();
  };

  const activeMeditation = activeMeditations[0] ?? null;
  const upcomingMeditations = meditations.filter(
    (m) => new Date(m.scheduled_at) > new Date(),
  );
  const hasUpcoming = upcomingMeditations.length > 0 || activeMeditations.length > 0;
  const nextMeditation = upcomingMeditations[0] ?? null;

  return {
    meditations,
    loading,
    hasUpcoming,
    nextMeditation,
    activeMeditation,
    activeMeditations,
    createMeditation,
    deleteMeditation,
    refetch: fetchMeditations,
  };
}
